import type { ArgumentStep, ComplexityArgumentSpec, ResolvedFigure, ShapeRole } from '../model.js';
import { box, finishFigure, point, SceneBuilder, validateViewport } from '../scene-builder.js';

const FAMILY = 'complexity-argument';

export function argumentStepId(stepId: string): string {
  return `argument.step.${stepId}`;
}

export function argumentExpressionId(stepId: string): string {
  return `${argumentStepId(stepId)}.expression`;
}

export function argumentRationaleId(stepId: string): string {
  return `${argumentStepId(stepId)}.rationale`;
}

/**
 * Resolves a coordinate-free proof into a stable worked-argument layout.
 * Each row keeps the expression and its reason together; the implication rail
 * carries derivation order rather than merely decorating the page.
 */
export function compileComplexityArgument(spec: ComplexityArgumentSpec): ResolvedFigure {
  validateComplexityArgument(spec);

  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const narrow = width <= 390;
  const margin = width <= 340 ? 12 : width <= 390 ? 16 : 28;
  const railWidth = narrow ? 22 : 30;
  const contentX = margin + railWidth;
  const contentWidth = width - contentX - margin;
  const claimY = narrow ? 39 : 42;
  const claimHeight = narrow ? 62 : 58;
  const rowsTop = claimY + claimHeight + (narrow ? 20 : 24);
  const footer = narrow ? 28 : 34;
  const rowGap = narrow ? 8 : 10;
  const rowHeight = (height - rowsTop - footer - rowGap * (spec.steps.length - 1)) / spec.steps.length;
  const warnings: string[] = [];

  builder.text('argument.eyebrow', 'argument', 'annotation', point(margin, 23), 'ANNOTATED ARGUMENT · EVERY STEP EARNS ITS ARROW', {
    size: narrow ? 8 : 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });

  builder.rect('argument.claim.surface', 'argument.claim', 'header', box(margin, claimY, width - margin * 2, claimHeight), {
    radius: 5,
    label: `Claim: ${spec.claim}`,
    interactive: true,
  });
  builder.text('argument.claim.label', 'argument.claim', 'annotation', point(margin + 12, claimY + 18), 'CLAIM TO PROVE', {
    size: narrow ? 8 : 9,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  const claimBudget = Math.max(18, Math.floor((width - margin * 2 - 24) / (narrow ? 6.2 : 7.2)));
  const claim = compactMiddle(spec.claim, claimBudget);
  builder.text('argument.claim.text', 'argument.claim', 'structure', point(margin + 12, claimY + 42), claim, {
    size: narrow ? 12 : 14,
    mono: true,
    weight: 700,
  });
  builder.anchor('argument.claim.support', 'argument.claim', point(width / 2, claimY + claimHeight), 'down');
  builder.hit('argument.claim', box(margin, claimY, width - margin * 2, claimHeight));
  if (claim !== spec.claim) warnings.push('Claim uses deterministic middle compaction at this width.');

  spec.steps.forEach((step, index) => {
    const y = rowsTop + index * (rowHeight + rowGap);
    drawStep(builder, spec, step, index, contentX, y, contentWidth, rowHeight, margin, warnings);

    if (index > 0) {
      const previous = spec.steps[index - 1]!;
      const previousY = rowsTop + (index - 1) * (rowHeight + rowGap);
      const railX = margin + railWidth / 2;
      builder.line(
        `${argumentStepId(step.id)}.derivation-arrow`,
        argumentStepId(step.id),
        'operator',
        point(railX, previousY + rowHeight),
        point(railX, y),
        { markerEnd: true },
      );
      builder.relation(
        `${argumentStepId(step.id)}.derived-from`,
        'derived-from',
        argumentStepId(previous.id),
        argumentStepId(step.id),
        step.rationale,
      );
    }
  });

  const conclusion = spec.steps.at(-1)!;
  builder.relation(
    'argument.conclusion.supports-claim',
    'supports-claim',
    argumentStepId(conclusion.id),
    'argument.claim',
    `${conclusion.expression} establishes ${spec.claim}`,
  );

  const active = [spec.focusStepId ? argumentStepId(spec.focusStepId) : 'argument', ...(spec.emphasis ?? [])];
  if (rowHeight < (narrow ? 62 : 54)) warnings.push('Argument rows are approaching the annotated-view density floor.');
  if (spec.steps.length > 6) warnings.push('Seven-step derivations should be revealed progressively in lessons.');

  return finishFigure(spec, builder, {
    title: 'A conclusion is only as strong as its derivation',
    subtitle: 'Read downward: each arrow is justified by the rationale attached to the expression it produces.',
    state: spec.focusStepId ? `focus:${spec.focusStepId}` : 'overview',
    active,
    warnings: [...new Set(warnings)],
  });
}

export function validateComplexityArgument(spec: ComplexityArgumentSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (!spec.provenance.trim()) throw new Error(`${FAMILY}: provenance is required`);
  if (!spec.claim.trim()) throw new Error(`${FAMILY}: claim is required`);
  if (spec.claim.length > 220) throw new Error(`${FAMILY}: claim exceeds 220 characters`);
  if (spec.steps.length < 3 || spec.steps.length > 7) throw new Error(`${FAMILY}: require 3…7 argument steps`);
  if (new Set(spec.steps.map((step) => step.id)).size !== spec.steps.length) throw new Error(`${FAMILY}: step IDs must be unique`);

  spec.steps.forEach((step, index) => {
    if (!validId(step.id)) throw new Error(`${FAMILY}: invalid step id ${step.id}`);
    if (!step.expression.trim()) throw new Error(`${FAMILY}: step ${step.id} requires an expression`);
    if (!step.rationale.trim()) throw new Error(`${FAMILY}: step ${step.id} requires a rationale`);
    if (step.expression.length > 240) throw new Error(`${FAMILY}: step ${step.id} expression exceeds 240 characters`);
    if (step.rationale.length > 280) throw new Error(`${FAMILY}: step ${step.id} rationale exceeds 280 characters`);
    const expectedKind: ArgumentStep['kind'] = index === 0 ? 'given' : index === spec.steps.length - 1 ? 'conclusion' : 'transform';
    if (step.kind !== expectedKind) {
      throw new Error(`${FAMILY}: step ${step.id} must be ${expectedKind}`);
    }
  });

  if (spec.focusStepId && !spec.steps.some((step) => step.id === spec.focusStepId)) {
    throw new Error(`${FAMILY}: focus step ${spec.focusStepId} does not exist`);
  }

  const rowGap = spec.viewport.width <= 390 ? 8 : 10;
  const fixed = (spec.viewport.width <= 390 ? 39 + 62 + 20 + 28 : 42 + 58 + 24 + 34) + rowGap * (spec.steps.length - 1);
  const rowHeight = (spec.viewport.height - fixed) / spec.steps.length;
  if (rowHeight < 46) throw new Error(`${FAMILY}: viewport height cannot fit ${spec.steps.length} readable steps`);
}

function drawStep(
  builder: SceneBuilder,
  spec: ComplexityArgumentSpec,
  step: ArgumentStep,
  index: number,
  x: number,
  y: number,
  width: number,
  height: number,
  margin: number,
  warnings: string[],
): void {
  const narrow = spec.viewport.width <= 390;
  const semanticId = argumentStepId(step.id);
  const expressionId = argumentExpressionId(step.id);
  const rationaleId = argumentRationaleId(step.id);
  const role = stepRole(step.kind);
  const chipWidth = narrow ? 35 : 46;
  const pad = narrow ? 9 : 12;
  const expressionColumn = narrow ? width - pad * 2 : Math.max(165, width * 0.45);
  const dividerX = narrow ? x + pad : x + expressionColumn;
  const label = step.kind === 'given' ? 'GIVEN' : step.kind === 'conclusion' ? 'THUS' : `STEP ${index}`;

  builder.rect(`${semanticId}.surface`, semanticId, role, box(x, y, width, height), {
    radius: 5,
    label: `${label}: ${step.expression}. Because ${step.rationale}`,
    interactive: true,
  });
  builder.rect(`${semanticId}.kind-chip`, semanticId, role, box(x + pad, y + 8, chipWidth, 18), { radius: 9, label });
  builder.text(`${semanticId}.kind`, semanticId, 'annotation', point(x + pad + chipWidth / 2, y + 21), label, {
    size: narrow ? 7 : 8,
    mono: true,
    weight: 700,
    align: 'middle',
    uppercase: true,
  });

  const expressionChars = narrow
    ? Math.max(17, Math.floor((width - pad * 2 - chipWidth - 7) / 6.15))
    : Math.max(18, Math.floor((expressionColumn - pad * 2) / 7));
  const expression = compactMiddle(step.expression, expressionChars);
  builder.text(
    `${expressionId}.text`,
    expressionId,
    step.kind === 'conclusion' ? 'positive' : 'structure',
    point(narrow ? x + pad + chipWidth + 7 : x + pad, narrow ? y + 21 : y + height / 2 + 5),
    expression,
    { size: narrow ? 10.5 : 12, mono: true, weight: 700 },
  );

  const rationaleChars = narrow
    ? Math.max(24, Math.floor((width - pad * 2) / 5.3))
    : Math.max(22, Math.floor((width - expressionColumn - pad * 2) / 5.5));
  const rationaleLines = wrapText(step.rationale, rationaleChars, narrow ? 2 : 3);
  const rationaleX = narrow ? x + pad : dividerX + pad;
  const rationaleFirstY = narrow ? y + 41 : y + height / 2 - ((rationaleLines.length - 1) * 12) / 2 + 3;
  rationaleLines.forEach((line, lineIndex) => {
    builder.text(`${rationaleId}.line.${lineIndex}`, rationaleId, 'annotation', point(rationaleX, rationaleFirstY + lineIndex * (narrow ? 11 : 12)), line, {
      size: narrow ? 8 : 9,
      mono: false,
      weight: 600,
    });
  });

  if (!narrow) {
    builder.line(`${semanticId}.reason-divider`, rationaleId, 'construction', point(dividerX, y + 10), point(dividerX, y + height - 10));
    builder.text(`${rationaleId}.because`, rationaleId, 'annotation', point(dividerX + pad, y + 17), 'BECAUSE', {
      size: 7,
      mono: true,
      weight: 700,
      uppercase: true,
    });
  }

  const railX = margin + (narrow ? 11 : 15);
  builder.line(`${semanticId}.rail-link`, semanticId, 'operator', point(railX, y + height / 2), point(x, y + height / 2));
  builder.anchor(`${semanticId}.input`, semanticId, point(railX, y), 'up');
  builder.anchor(`${semanticId}.output`, semanticId, point(railX, y + height), 'down');
  builder.anchor(`${expressionId}.center`, expressionId, point(x + (narrow ? width / 2 : expressionColumn / 2), y + height / 2));
  builder.anchor(`${rationaleId}.center`, rationaleId, point(narrow ? x + width / 2 : dividerX + (width - expressionColumn) / 2, narrow ? y + height - 15 : y + height / 2));
  builder.hit(semanticId, box(x, y, width, height));
  builder.relation(`${rationaleId}.justifies`, 'justifies', rationaleId, expressionId, step.rationale);

  if (expression !== step.expression) warnings.push(`Expression compacted: ${step.id}`);
  if (rationaleLines.join(' ').replace(/…$/, '') !== step.rationale) warnings.push(`Rationale compacted: ${step.id}`);
}

function stepRole(kind: ArgumentStep['kind']): ShapeRole {
  if (kind === 'given') return 'header';
  if (kind === 'conclusion') return 'positive';
  return 'surface';
}

function compactMiddle(value: string, maxChars: number): string {
  const normalized = value.trim().replace(/\s+/g, ' ');
  if (normalized.length <= maxChars) return normalized;
  const head = Math.max(2, Math.ceil((maxChars - 1) * 0.58));
  const tail = Math.max(1, maxChars - head - 1);
  return `${normalized.slice(0, head)}…${normalized.slice(-tail)}`;
}

function wrapText(value: string, maxChars: number, maxLines: number): string[] {
  const words = value.trim().replace(/\s+/g, ' ').split(' ');
  const lines: string[] = [];
  let line = '';
  let consumed = 0;
  for (const word of words) {
    const next = line ? `${line} ${word}` : word;
    if (next.length <= maxChars) {
      line = next;
      consumed += 1;
    } else {
      if (line) lines.push(line);
      line = word.length <= maxChars ? word : compactMiddle(word, maxChars);
      consumed += 1;
      if (lines.length === maxLines - 1) break;
    }
  }
  if (line && lines.length < maxLines) lines.push(line);
  if (consumed < words.length && lines.length) {
    const last = lines.length - 1;
    lines[last] = compactMiddle(`${lines[last]} ${words.slice(consumed).join(' ')}`, maxChars);
  }
  return lines;
}

function validId(value: string): boolean {
  return /^[a-z][a-z0-9-]*$/.test(value);
}
